import styles from "./styles.scss";
import clsx from "clsx";
import {
  cloneElement,
  forwardRef,
  MouseEventHandler,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { autoUpdate, useFloating, useMergeRefs } from "@floating-ui/react";
import { getApplicationRoot } from "@/utils";
import { getArrowPlacementSide } from "../popup-menu/utils";
import { TooltipVariant } from "@/constants/tooltip";
import { getFloatingMiddlewares } from "./utils";
import { TooltipProps } from "./types";

function composeEventHandler(
  handler: MouseEventHandler,
  eventHandler: MouseEventHandler,
): MouseEventHandler {
  return event => {
    if (eventHandler) {
      eventHandler(event);
    }

    handler(event);
  };
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      children,
      title,
      className,
      arrowClassName,
      delay = 500,
      // floatingOptions,
      externalMiddleware = [],
      hideArrow,
      portalRef,
      variant = TooltipVariant.Dark,
    },
    forwardedRef,
  ) => {
    const isMobileApp = false; // checkIsMobileApp();
    const childrenProps = children.props;
    const isSecondary = variant === TooltipVariant.Secondary;
    const isDark = variant === TooltipVariant.Dark;

    const [isTooltipOpened, setIsTooltipOpened] = useState(false);

    const openTimeout = useRef<ReturnType<typeof setTimeout>>();
    const closeTimeout = useRef<ReturnType<typeof setTimeout>>();
    const arrowRef = useRef<HTMLDivElement>(null);
    const tooltipLocalRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
      const tooltipDiv = tooltipLocalRef.current;

      if (!tooltipDiv) {
        return;
      }

      if (!title) {
        setIsTooltipOpened(false);
      }

      const handleTooltipMouseOver = () => {
        closeTimeout.current && clearTimeout(closeTimeout.current);
        setIsTooltipOpened(true);
      };

      const handleTooltipMouseOut = () => {
        setIsTooltipOpened(false);
      };

      if (isTooltipOpened) {
        tooltipDiv.addEventListener("mouseover", handleTooltipMouseOver);
        tooltipDiv.addEventListener("mouseout", handleTooltipMouseOut);
      }

      return () => {
        if (isTooltipOpened && tooltipDiv) {
          tooltipDiv.removeEventListener("mouseover", handleTooltipMouseOver);
          tooltipDiv.removeEventListener("mouseout", handleTooltipMouseOut);
        }
      };
    }, [isTooltipOpened, title]);

    const {
      x,
      y,
      refs: { setReference, setFloating },
      placement,
      strategy,
      middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
    } = useFloating({
      placement: "top",
      middleware: [
        ...getFloatingMiddlewares({ arrowElement: arrowRef }),
        ...externalMiddleware,
      ],
      whileElementsMounted: autoUpdate,
      // ...floatingOptions,
    });

    const arrowPlacementSide = getArrowPlacementSide(placement);
    const componentRef = useMergeRefs([forwardedRef, setReference]);
    const tooltipRef = useMergeRefs([setFloating, tooltipLocalRef]);
    const portalRoot = portalRef?.current || getApplicationRoot();

    const handleMouseOver: MouseEventHandler<HTMLElement> = event => {
      event.currentTarget.removeAttribute("title");

      if (event?.target) {
        (event.target as HTMLElement).removeAttribute("title");
      }

      closeTimeout.current && clearTimeout(closeTimeout.current);

      openTimeout.current = setTimeout(() => {
        setIsTooltipOpened(true);
      }, delay);
    };

    const handleMouseOut = () => {
      closeTimeout.current = setTimeout(() => setIsTooltipOpened(false), delay);
      openTimeout.current && clearTimeout(openTimeout.current);
    };

    const newProps = {
      ...childrenProps,
      ref: componentRef,
      onMouseOver: isMobileApp
        ? undefined
        : composeEventHandler(handleMouseOver, childrenProps.onMouseOver),
      onMouseOut: isMobileApp
        ? undefined
        : composeEventHandler(handleMouseOut, childrenProps.onMouseOut),
    };

    const renderTooltip = () => (
      <div
        ref={tooltipRef}
        style={{
          position: strategy,
          top: y ?? "",
          left: x ?? "",
        }}
        className={clsx(
          styles.tooltip,
          isSecondary && styles.backgroundSecondary,
          isDark && styles.darkVariant,
          className,
        )}
      >
        <span className={styles.tooltipText}>{title}</span>

        {!hideArrow && (
          <div
            ref={arrowRef}
            style={{
              top: arrowY ?? "",
              left: arrowX ?? "",
              [arrowPlacementSide]: -4,
            }}
            className={clsx(
              styles.tooltipArrow,
              isSecondary && styles.backgroundSecondary,
              arrowClassName,
            )}
          />
        )}
      </div>
    );

    return (
      <>
        {cloneElement(children, newProps)}
        {isTooltipOpened && createPortal(renderTooltip(), portalRoot)}
      </>
    );
  },
);

Tooltip.displayName = "Tooltip";
